<template>
    <div class="answer-input" :class="{ 'exam-mode': examMode, 'light-mode': isLightMode && !examMode }">
        <label for="answer">Your Answer:</label>

        <div class="answer-row" ref="answerRowRef">
            <textarea
                id="answer"
                :value="modelValue"
                @input="$emit('update:modelValue', $event.target.value)"
                rows="5"
                placeholder="Type your answer here..."
                :disabled="disabled"
                :style="{ width: hasLatex ? textareaWidth : '100%' }"
                :class="{ 'exam-mode': examMode, 'light-mode': isLightMode && !examMode }"
            ></textarea>

            <div
                v-if="hasLatex"
                class="resize-handle"
                :class="{ 'exam-mode': examMode, 'light-mode': isLightMode && !examMode }"
                @mousedown="startResize"
            ></div>

            <div
                v-if="hasLatex"
                class="latex-preview"
                :class="{ 'exam-mode': examMode, 'light-mode': isLightMode && !examMode }"
                ref="previewRef"
                :style="{ width: previewWidth }"
                :key="previewKey"
            >
                <div
                    v-for="(line, idx) in previewLines"
                    :key="`${idx}-${line}`"
                    class="preview-line"
                    @click="(e) => handlePreviewClick(e, idx)"
                >
                    <span v-if="line" v-html="formatText(line)"></span>
                </div>
            </div>
        </div>

        <div class="math-live-container" :class="{ 'exam-mode': examMode, 'light-mode': isLightMode && !examMode }">
            <math-field
                ref="mathfield"
                id="mathfield"
                :class="{ 'exam-mode': examMode, 'light-mode': isLightMode && !examMode }"
                :placeholder="editingEquation ? '' : '\\text{ Type your equations here...}'"
            ></math-field>
            <button
                class="mf-insert-btn"
                :class="{ editing: editingEquation, 'exam-mode': examMode, 'light-mode': isLightMode && !examMode }"
                @click="editingEquation ? confirmEdit() : insertMath()"
                :title="editingEquation ? 'Confirm edit (Enter)' : `Insert ${insertMode} equation (Enter)`"
            >
                <span v-if="editingEquation">✓</span>
                <CornerDownLeft v-else :size="16" />
            </button>
            <button
                v-if="editingEquation"
                class="mf-cancel-btn"
                :class="{ 'exam-mode': examMode, 'light-mode': isLightMode && !examMode }"
                @click="cancelEdit"
                title="Cancel edit (Escape)"
            >
                ✕
            </button>
            <button
                v-else
                class="mf-mode-btn"
                :class="{ 'exam-mode': examMode, 'light-mode': isLightMode && !examMode }"
                @click="toggleInsertMode"
                :title="`Switch to ${insertMode === 'inline' ? 'display' : 'inline'} mode (Shift+Enter for ${insertMode === 'inline' ? 'display' : 'inline'})`"
            >
                {{ insertMode === 'inline' ? 'inline' : 'display' }}
            </button>
            <button
                class="mf-toggle-canvas-btn"
                :class="{ active: canvasVisible, noHover: noHover, disabled: !canUseCanvas, 'exam-mode': examMode, 'light-mode': isLightMode && !examMode }"
                @click="handleToggle"
                @mouseleave="noHover = false"
                :title="!canUseCanvas ? 'Upgrade to Student plan to use canvas' : 'Toggle drawing canvas'"
            >
                CANVAS
                <svg v-if="!canUseCanvas" class="lock-icon" width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
                </svg>
            </button>
        </div>

        <slot name="canvas"></slot>
    </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue';
import 'mathlive';
import { useMarkdownRenderer } from '@/composables/useMarkdownRenderer';
import { useAuthStore } from '@/stores/auth';
import { CornerDownLeft } from 'lucide-vue-next';

const { formatText, renderMath } = useMarkdownRenderer();
const authStore = useAuthStore();

// Exam mode requires Tier 2+ subscription
const examMode = computed(() => {
    const hasTier = authStore.hasTierAccess('TIER 2');
    const isEnabled = authStore.settings?.appearance?.exam_mode ?? false;
    return hasTier && isEnabled;
});

const theme = computed(() => authStore.settings?.appearance?.theme ?? 'dark');

// Light mode = exam mode OR light theme
const isLightMode = computed(() => {
    if (examMode.value) return true;
    if (theme.value === 'light') return true;
    if (theme.value === 'auto') {
        return window.matchMedia?.('(prefers-color-scheme: light)').matches ?? false;
    }
    return false;
});

const props = defineProps({
    modelValue: String,
    disabled: Boolean,
    canvasVisible: Boolean,
    canUseCanvas: {
        type: Boolean,
        default: true
    }
});

const emit = defineEmits(['update:modelValue', 'toggle-canvas', 'math-enter']);

const mathfield = ref(null);
const previewRef = ref(null);
const answerRowRef = ref(null);
const noHover = ref(false);

// Insert mode: 'inline' or 'block'
const insertMode = ref('inline');

// Editing state
const editingEquation = ref(null);

// Resize state
const previewWidth = ref('33%');
const isResizing = ref(false);

// Computed: textarea width based on preview (no gap since resize handle uses negative margins)
const textareaWidth = computed(() => {
    return `calc(100% - ${previewWidth.value})`;
});

// Computed: check if modelValue contains any LaTeX
const hasLatex = computed(() => {
    const val = props.modelValue || '';
    return /\$\$.+?\$\$|\$[^$]+\$|\\\(.+?\\\)|\\\[.+?\\\]/s.test(val);
});

// Computed: process lines for preview (hybrid approach)
const previewLines = computed(() => {
    const lines = (props.modelValue || '').split('\n');
    return lines.map(line => {
        const trimmed = line.trim();

        // Check if line is ONLY display math ($$...$$ or \[...\])
        const isDisplayMathOnly = /^\$\$.+\$\$$|^\\\[.+\\\]$/.test(trimmed);

        // Check if line has any LaTeX at all
        const lineHasLatex = /\$\$.+?\$\$|\$[^$]+\$|\\\(.+?\\\)|\\\[.+?\\\]/s.test(line);

        if (!lineHasLatex) {
            return ''; // No LaTeX, empty preview line
        } else if (isDisplayMathOnly) {
            return trimmed; // Display math only - show equation alone
        } else {
            return line; // Inline math - show full line with context
        }
    });
});

// Key to force full re-render of preview when content changes
const previewKey = computed(() => {
    // Include content hash to force re-render when any equation changes
    const content = previewLines.value.join('|');
    return `preview-${content.length}-${content.substring(0, 100)}`;
});

// Parse equations with their positions in the text
const parseEquations = (text) => {
    const equations = [];
    const regex = /\$\$[\s\S]+?\$\$|\$[^$]+\$|\\\([\s\S]+?\\\)|\\\[[\s\S]+?\\\]/g;
    let match;
    while ((match = regex.exec(text)) !== null) {
        equations.push({
            latex: match[0],
            start: match.index,
            end: match.index + match[0].length,
            // Extract inner content (without delimiters)
            inner: match[0].replace(/^\$\$|\$\$$|^\$|\$$|^\\\(|\\\)$|^\\\[|\\\]$/g, '')
        });
    }
    return equations;
};

// Handle click on preview to edit equation
const handlePreviewClick = (event, lineIdx) => {
    const clickedEl = event.target.closest('.katex');
    if (!clickedEl) return;

    // Find which equation was clicked
    const text = props.modelValue || '';
    const equations = parseEquations(text);

    // Get the line's equations
    const lines = text.split('\n');
    let charOffset = 0;
    for (let i = 0; i < lineIdx; i++) {
        charOffset += lines[i].length + 1; // +1 for newline
    }
    const lineEnd = charOffset + (lines[lineIdx]?.length || 0);

    // Find equations on this line
    const lineEquations = equations.filter(eq => eq.start >= charOffset && eq.end <= lineEnd);

    // Find the clicked equation (by index within the line's rendered katex elements)
    const previewLine = previewRef.value?.children[lineIdx];
    if (!previewLine) return;

    const katexElements = previewLine.querySelectorAll('.katex');
    const clickedIndex = Array.from(katexElements).indexOf(clickedEl);

    if (clickedIndex >= 0 && clickedIndex < lineEquations.length) {
        const eq = lineEquations[clickedIndex];
        startEditingEquation(eq);
    }
};

// Start editing an equation
const startEditingEquation = (eq) => {
    editingEquation.value = eq;
    const mf = document.getElementById('mathfield');
    if (mf) {
        mf.value = eq.inner;
        mf.focus();
    }
};

// Confirm the edit
const confirmEdit = () => {
    const mf = document.getElementById('mathfield');
    const newInner = mf?.value.trim();

    if (!newInner || !editingEquation.value) {
        cancelEdit();
        return;
    }

    const eq = editingEquation.value;
    const text = props.modelValue || '';

    // Preserve the original delimiter type
    let newLatex;
    if (eq.latex.startsWith('$$')) {
        newLatex = `$$${newInner}$$`;
    } else if (eq.latex.startsWith('\\[')) {
        newLatex = `\\[${newInner}\\]`;
    } else if (eq.latex.startsWith('\\(')) {
        newLatex = `\\(${newInner}\\)`;
    } else {
        newLatex = `$${newInner}$`;
    }

    const newText = text.substring(0, eq.start) + newLatex + text.substring(eq.end);
    emit('update:modelValue', newText);

    mf.value = '';
    editingEquation.value = null;
};

// Cancel editing
const cancelEdit = () => {
    const mf = document.getElementById('mathfield');
    if (mf) mf.value = '';
    editingEquation.value = null;
};

// Toggle insert mode
const toggleInsertMode = () => {
    insertMode.value = insertMode.value === 'inline' ? 'block' : 'inline';
};

// Handle canvas toggle
const handleToggle = () => {
    if (!props.canUseCanvas) {
        return;
    }

    if (props.canvasVisible) {
        noHover.value = true;
    } else {
        noHover.value = false;
    }

    emit('toggle-canvas');
};

// Insert math with current mode
const insertMath = (forceMode = null) => {
    const mf = document.getElementById('mathfield');
    if (!mf) return;

    // MathLive's value property
    const value = (mf.value || '').trim();
    if (!value) return;

    const mode = forceMode || insertMode.value;
    const latex = mode === 'inline' ? `$${value}$` : `$$${value}$$`;

    emit('math-enter', latex);

    // Clear the mathfield
    try {
        mf.value = '';
    } catch (err) {
        // Fallback for older MathLive versions
        mf.setValue?.('');
    }
};

// Resize handlers
const startResize = (e) => {
    isResizing.value = true;
    document.addEventListener('mousemove', onResize);
    document.addEventListener('mouseup', stopResize);
    e.preventDefault();
};

const onResize = (e) => {
    const container = answerRowRef.value;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const newPreviewWidth = containerRect.right - e.clientX;
    const percentage = (newPreviewWidth / containerRect.width) * 100;

    // Clamp between 20% and 60%
    previewWidth.value = `${Math.max(20, Math.min(60, percentage))}%`;
};

const stopResize = () => {
    isResizing.value = false;
    document.removeEventListener('mousemove', onResize);
    document.removeEventListener('mouseup', stopResize);
};

// Watch modelValue to re-render math (debounced to avoid race conditions)
let renderTimeout = null;
watch(() => props.modelValue, async () => {
    // Clear any pending render
    if (renderTimeout) {
        clearTimeout(renderTimeout);
    }

    // Wait for Vue to finish DOM updates, then render math
    await nextTick();
    renderTimeout = setTimeout(() => {
        if (previewRef.value) {
            renderMath(previewRef.value);
        }
    }, 10);
}, { immediate: true });

onMounted(() => {
    const mf = document.getElementById('mathfield');
    if (!mf) return;

    customElements.whenDefined('math-field').then(() => {
        mf.virtualKeyboardMode = 'manual';
        mf.smartFence = true;
        mf.smartMode = true;
        mf.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                if (editingEquation.value) {
                    confirmEdit();
                } else if (e.shiftKey) {
                    insertMath('block');
                } else {
                    insertMath();
                }
            } else if (e.key === 'Escape' && editingEquation.value) {
                cancelEdit();
            }
        });
    });
});

onUnmounted(() => {
    document.removeEventListener('mousemove', onResize);
    document.removeEventListener('mouseup', stopResize);
    if (renderTimeout) {
        clearTimeout(renderTimeout);
    }
});
</script>

<style scoped>
.answer-input {
    margin-bottom: 30px;
}

.answer-input label {
    display: block;
    margin-bottom: 10px;
    font-weight: 600;
    color: var(--text-secondary);
}

/* Answer row: textarea + resize handle + preview */
.answer-row {
    display: flex;
    gap: 0;
    align-items: stretch;
}

.answer-input textarea {
    padding: 10px;
    border: 2px solid rgba(var(--accent-rgb), 0.4);
    font-size: 15px;
    font-family: inherit;
    resize: vertical;
    box-sizing: border-box;
    background: var(--bg-primary);
    color: var(--text-primary);
    line-height: 1.5;
    min-height: 120px;
}

.answer-input textarea:focus {
    outline: none;
    border-color: var(--accent);
}

/* Resize handle - invisible but functional */
.resize-handle {
    width: 8px;
    cursor: ew-resize;
    background: transparent;
    border: none;
    margin: 0 -4px; /* Negative margin to overlap and make boxes adjacent */
    position: relative;
    z-index: 10;
    flex-shrink: 0;
}

.resize-handle:hover {
    background: transparent;
}

/* LaTeX preview panel */
.latex-preview {
    border: 2px solid rgba(var(--accent-rgb), 0.4);
    border-left: none; /* Share border with textarea */
    background: var(--bg-primary);
    color: var(--text-primary);
    padding: 10px;
    box-sizing: border-box;
    overflow-y: auto;
    overflow-x: hidden;
    min-height: 120px;
    max-height: 300px;
}

.preview-line {
    min-height: 22.5px; /* matches textarea line-height: 1.5 * 15px font-size */
    line-height: 1.5;
    font-size: 15px;
    display: flex;
    align-items: center;
}

.preview-line:empty::before {
    content: '\00a0'; /* non-breaking space to maintain height */
}

/* Make equations clickable */
.preview-line :deep(.katex) {
    cursor: pointer;
    padding: 2px 4px;
    border-radius: 3px;
    transition: background 0.15s;
}

.preview-line :deep(.katex:hover) {
    background: rgba(var(--accent-rgb), 0.15);
}

/* Prevent line wrapping in equations */
.preview-line :deep(.katex-display) {
    margin: 0;
    white-space: nowrap;
    overflow-x: auto;
}

.math-live-container {
    margin: 10px 0;
    display: flex;
    gap: 6px;
}

#mathfield {
    font-size: 20px;
    border: 2px solid rgba(var(--accent-rgb), 0.4);
    display: block;
    width: 100%;
    background: var(--bg-primary);
    color: var(--text-primary);
}

#mathfield:focus {
    border-color: var(--accent) !important;
    outline: none !important;
}

.mf-insert-btn {
    background: var(--bg-secondary);
    color: var(--text-primary);
    padding: 8px 12px;
    border: 2px solid rgba(var(--accent-rgb), 0.4);
    cursor: pointer;
    font-weight: 600;
    transition: all 0.2s;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    white-space: nowrap;
}

.mf-insert-btn:hover {
    border-color: var(--accent);
}

.mf-insert-btn.editing {
    background: var(--accent);
    color: var(--bg-primary);
    border-color: var(--accent);
}

.mf-cancel-btn,
.mf-mode-btn {
    background: var(--bg-secondary);
    color: var(--text-secondary);
    padding: 8px 10px;
    border: 2px solid rgba(var(--accent-rgb), 0.4);
    cursor: pointer;
    font-weight: 600;
    transition: all 0.2s;
    font-size: 14px;
    text-transform: uppercase;
}

.mf-cancel-btn:hover {
    border-color: #e74c3c;
    color: #e74c3c;
}

.mf-mode-btn:hover {
    border-color: var(--accent);
    color: var(--accent);
}

.mf-toggle-canvas-btn {
    background: var(--bg-secondary);
    color: var(--text-secondary);
    padding: 8px 8px;
    border: 2px solid rgba(var(--accent-rgb), 0.4);
    cursor: pointer;
    font-weight: 600;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 6px;
    letter-spacing: 1.2px;
}

.mf-toggle-canvas-btn {
    transition:
        background 0.2s ease,
        border-color 0.2s ease,
        transform 0.1s ease;
}

.mf-toggle-canvas-btn:hover,
.mf-toggle-canvas-btn.active {
    background: var(--bg-secondary);
    border-color: var(--accent);
}

.mf-toggle-canvas-btn.noHover:hover {
    background: var(--bg-secondary);
    border-color: var(--accent);
}

.mf-toggle-canvas-btn:active {
    transform: scale(0.97);
}

.mf-toggle-canvas-btn.disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.mf-toggle-canvas-btn.disabled:hover {
    border-color: rgba(var(--accent-rgb), 0.4);
}

.mf-toggle-canvas-btn .lock-icon {
    margin-left: 2px;
    opacity: 0.7;
}

/* Exam mode styles */
.answer-input textarea.exam-mode {
    background: #ffffff;
    color: #000000;
    border-color: #cccccc;
    font-family: 'Times New Roman', Times, serif;
}

.answer-input textarea.exam-mode:focus {
    border-color: #666666;
}

.latex-preview.exam-mode {
    background: #ffffff;
    color: #000000;
    border: 1px solid #000000;
    border-left: none;
    font-family: 'Times New Roman', Times, serif;
}

/* Resize handle in exam mode - still invisible */
.resize-handle.exam-mode {
    background: transparent;
}

.resize-handle.exam-mode:hover {
    background: transparent;
}

.math-live-container.exam-mode #mathfield {
    background: #ffffff;
    color: #000000;
    border-color: #cccccc;
}

.math-live-container.exam-mode #mathfield:focus {
    border-color: #666666 !important;
}

.mf-insert-btn.exam-mode,
.mf-cancel-btn.exam-mode,
.mf-mode-btn.exam-mode {
    background: #ffffff;
    color: #333333;
    border: 1px solid #000000;
    font-family: 'Times New Roman', Times, serif;
}

.mf-insert-btn.exam-mode:hover,
.mf-mode-btn.exam-mode:hover {
    background-color: #f5f5f5;
}

.mf-insert-btn.editing.exam-mode {
    background: #333333;
    color: #ffffff;
    border-color: #333333;
}

.mf-cancel-btn.exam-mode:hover {
    border-color: #e74c3c;
    color: #e74c3c;
}

.mf-toggle-canvas-btn.exam-mode {
    background: #f5f5f5;
    color: #333333;
    border-color: #cccccc;
    font-family: 'Times New Roman', Times, serif;
}

.mf-toggle-canvas-btn.exam-mode:hover,
.mf-toggle-canvas-btn.exam-mode.active {
    border-color: #666666;
}

.answer-input.exam-mode label {
    color: #333333;
    font-family: 'Times New Roman', Times, serif;
}

/* Light mode styles (non-exam) */
.answer-input textarea.light-mode {
    background: var(--bg-primary);
    color: var(--text-primary);
    border-color: rgba(var(--accent-rgb), 0.4);
}

.latex-preview.light-mode {
    background: var(--bg-primary);
    color: var(--text-primary);
    border-color: rgba(var(--accent-rgb), 0.4);
    border-left: none;
}

/* Resize handle in light mode - still invisible */
.resize-handle.light-mode {
    background: transparent;
}

.resize-handle.light-mode:hover {
    background: transparent;
}

.mf-insert-btn.light-mode,
.mf-cancel-btn.light-mode,
.mf-mode-btn.light-mode {
    background: var(--bg-secondary);
    color: var(--text-primary);
    border-color: rgba(var(--accent-rgb), 0.4);
}

.mf-insert-btn.light-mode:hover,
.mf-mode-btn.light-mode:hover {
    border-color: var(--accent);
}

.mf-insert-btn.editing.light-mode {
    background: var(--accent);
    color: var(--bg-primary);
    border-color: var(--accent);
}

</style>